import {
  Flex,
  Table,
  Checkbox,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Circle,
  Button,
  IconButton,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { TriangleDownIcon, TriangleUpIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import axios from "axios";

// Custom components
import Card from "components/card/Card";
import Menu from "components/menu/MainMenu";
import NewChip from "views/admin/chipstats/components/NewChip";
import EditChip from "views/admin/chipstats/components/EditChip";

// Utils
import { formatLastSeen } from "utils/dateUtils";

export default function CheckTable(props) {
  const { columnsData, tableData, setTableData, departmentsData, setAlert, organizationsData } = props;

  const columns = useMemo(() => columnsData, [columnsData]);
  const departments = useMemo(() => departmentsData, [departmentsData]);
  const organizations = useMemo(() => organizationsData, [organizationsData]);
  const data = useMemo(() => tableData, [tableData]);

  const authToken = localStorage.getItem('authToken');
  const userData = JSON.parse(localStorage.getItem('userData'));

  const menuItems = departments.map((dep) => ({
    icon: dep.icon,
    label: dep.name,
    onClick: () => handleMoveToDepartment(dep),
  }));

  const [selectedChipIds, setSelectedChipIds] = useState([]);

  const allChipIds = data.map((chip) => chip._id.$oid);
  const allSelected = allChipIds.length > 0 && allChipIds.every(id => selectedChipIds.includes(id));

  const toggleAll = () => {
    if (allSelected || (selectedChipIds.length > 0 && selectedChipIds.length < allChipIds.length)) {
      setSelectedChipIds([]);
    } else {
      setSelectedChipIds(allChipIds);
    }
  };

  const toggleOne = (id) => {
    setSelectedChipIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleMoveToDepartment = async (newDepartment) => {
    const newDepartmentId = newDepartment._id.$oid;

    const changedChips = data
      .filter((chip) =>
        selectedChipIds.includes(chip._id.$oid) &&
        chip.department.$oid !== newDepartmentId
      );

    try {
      await Promise.all(
        changedChips.map((chip) => {
          if (chip.department.$oid === newDepartmentId) {
            setAlert([
              'warning',
              'Advarsel',
            `Chip ${chip.chip_id} er allerede i ${newDepartment.name}`
          ]);
          return Promise.resolve();
        }

        let body = {
          department: newDepartmentId,
        };

        if (newDepartment.organization.$oid !== chip.organization.$oid) {
          body.organization = newDepartment.organization.$oid;
        }

        return axios.put(
            `https://api.beehiveproject.no/chip/${chip._id.$oid}`,
            body,
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          )
          .then(() => {
            setAlert([
              'success',
              'Suksess',
              `Chip ${chip.chip_id} har blitt flyttet til ${newDepartment.name}`
            ]);
          })
          .catch((err) => {
            console.error(`Error updating chip ${chip.chip_id}:`, err);
            setAlert([
              'error',
              'Feil',
              `Kunne ikke flytte chip ${chip.chip_id} til ${newDepartment.name}`
            ])
            }
          );
      }),
    );

    // TODO: re fetch the chips to update the table instead
    // TODO: Copy this to chiptable.js
    const updatedData = data.map((chip) => {
      if (selectedChipIds.includes(chip._id.$oid)) {
        const newDepartment = departments.find(
            (dep) => dep._id.$oid === chip.department?.$oid
          );

        return {
          ...chip,
          department: {
            $oid: newDepartmentId,
            name: newDepartment.name || "Ukjent avdeling",
          },
          organization: {
            $oid: newDepartment.organization.$oid,
            name: newDepartment.organization.name || "Ukjent organisasjon",
          },
        };
      }

      return chip;
    });

    setTableData(updatedData);
    setSelectedChipIds([]);
    } catch (error) {
      console.error("Error moving chips:", error);
      setAlert(['error', 'Feil', 'En feil oppsto under flytting av chipper']);
    }
  }

  // Edit-modal state
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const [editingChip, setEditingChip] = useState(null);

  const handleEditChip = (chip) => {
    setEditingChip(chip);
    onEditOpen();
  };

  // Wraps onEditClose to ALSO clear editingChip — this forces an additional
  // state change in CheckTable on every close path (Save, Cancel, X, overlay).
  // Save-path used to "wake up" Chakra via setTableData re-render; this gives
  // Cancel/X-paths the same parent re-render so the modal cleans up properly.
  const handleEditClose = () => {
    onEditClose();
    setEditingChip(null);
  };

  const handleChipUpdated = (updatedChip) => {
    setTableData((prev) =>
      prev.map((c) => (c._id.$oid === updatedChip._id.$oid ? updatedChip : c))
    );
  };

  // Delete (window.confirm to avoid stacking Chakra modals)
  const handleDeleteChip = (chip) => {
    const ok = window.confirm(
      `Er du sikker på at du vil slette chip "${chip.chip_id}"? Denne handlingen kan ikke angres.`
    );
    if (!ok) return;

    axios
      .delete(`https://api.beehiveproject.no/chip/${chip._id.$oid}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.status === 200 || response.status === 204) {
          setTableData((prev) =>
            prev.filter((c) => c._id.$oid !== chip._id.$oid)
          );
          setSelectedChipIds((prev) =>
            prev.filter((id) => id !== chip._id.$oid)
          );
          setAlert(["success", "Suksess", `Chip ${chip.chip_id} ble slettet`]);
        } else {
          setAlert([
            "error",
            "Feil",
            `Kunne ikke slette chip ${chip.chip_id}`,
          ]);
        }
      })
      .catch((err) => {
        console.error("Failed to delete chip:", err);
        setAlert([
          "error",
          "Feil",
          `Kunne ikke slette chip ${chip.chip_id}`,
        ]);
      });
  };

  const tableInstance = useTable(
    {
      columns,
      data,
      autoResetSortBy: false,
      autoResetFilters: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    initialState,
  } = tableInstance;
  initialState.pageSize = 64;

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  return (
    <Card
      direction='column'
      w='100%'
      px='0px'
      overflowX={{ sm: "scroll", lg: "hidden" }}>
      <Flex px='25px' justify='space-between' align='center'>
        <Flex align='center' gap='10px' py='10px'>
          <Text
            color={textColor}
            fontSize='22px'
            fontWeight='700'
            lineHeight='100%'>
            Chipper
          </Text>
          <Text color={textColor} fontSize='22px' fontWeight='700'>
            {data.length}
          </Text>
        </Flex>
        <Flex align='center' gap='20px'>
          {selectedChipIds.length > 0 ? (
            <Flex align='center' gap='20px' bg='brand.100' px='10px' py='5px' borderRadius='10px'>
              <Text color={textColor} fontSize='sm' fontWeight='700' ps="2">Flytt {selectedChipIds.length} {selectedChipIds.length === 1 ? "chip" : "chipper"}</Text>
              <Menu items={menuItems} />
            </Flex>
          ) : (
            <></>
          )}
          {userData['is_super_user'] && 
            <NewChip setAlert={setAlert} organizationsData={organizations} />
          }
        </Flex>
      </Flex>
      <Table {...getTableProps()} variant='simple' color='gray.500' mb='24px'>
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, index) => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  key={index}
                  borderColor={borderColor}>
                  {column.Header === "CHECKBOX" ? (
                    <Flex align='center' justify='center'>
                      <Checkbox 
                        isChecked={allSelected}
                        isIndeterminate={
                            selectedChipIds.length > 0 && 
                            selectedChipIds.length < allChipIds.length
                          }
                        name="all_chips"
                        colorScheme="brandScheme"
                        onChange={toggleAll} 
                      />
                    </Flex>
                  ) : (
                    <Flex
                      justify="space-between"
                      align="center"
                      fontSize={{ sm: "10px", lg: "12px" }}
                      color="gray.400"
                      width="100%"
                    >
                      <Text>{column.render("Header")}</Text>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <TriangleDownIcon boxSize={3} />
                        ) : (
                          <TriangleUpIcon boxSize={3} />
                        )
                      ) : null}
                    </Flex>                    
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {tableData.length === 0 ? (
            <Tr>
              <Td colSpan={columns.length} textAlign="center" py={10}>
                <Text fontSize="md" color="gray.500">
                  Ingen data tilgjengelig
                </Text>
              </Td>
            </Tr>
          ) : (
            page.map((row, index) => {
              prepareRow(row);
              return (
                <Tr {...row.getRowProps()} key={index}>
                  {row.cells.map((cell, index) => {
                    let data = "";
                    const chipName = row.original.chip_id;
                    const chipId = row.original._id.$oid;

                    if (cell.column.Header === "CHECKBOX") {
                      data = (
                        <Flex align='center' justify='center'>
                          <Checkbox
                            isChecked={selectedChipIds.includes(chipId)}
                            name={chipId}
                            colorScheme='brandScheme'
                            onChange={() => toggleOne(chipId)}
                          />
                        </Flex>
                      );
                    } else if (cell.column.Header === "NAVN") {
                      data = (
                        <Flex align='center'>
                          <Text color={textColor} fontSize='sm' fontWeight='700'>
                            {chipName}
                          </Text>
                        </Flex>
                      );
                    } else if (cell.column.Header === "ORGANISASJON") {
                      data = (
                        <Flex align='center'>
                          <Text
                            me='10px'
                            color={textColor}
                            fontSize='sm'
                            fontWeight='700'>
                            {cell.row.original.organization.name || "Ukjent organisasjon"}
                          </Text>
                        </Flex>
                      );
                    } else if (cell.column.Header === "AVDELING") {
                      data = (
                        <Flex align='center'>
                          <Text
                            me='10px'
                            color={textColor}
                            fontSize='sm'
                            fontWeight='700'>
                            {cell.row.original.department.name}
                          </Text>
                        </Flex>
                      );
                    } else if (cell.column.Header === "ONLINE") {
                      const isConnected = cell.row.original.is_connected;
                      data = (
                        <Flex align="center" gap="2">
                          <Circle size="10px" bg={isConnected ? "green.400" : "red.400"} />
                          <Text color={textColor} fontSize="sm" fontWeight="700">
                            {isConnected ? "Ja" : "Nei"}
                          </Text>
                        </Flex>
                      );
                    } else if (cell.column.Header === "SIST SETT") {
                      const unixTime = cell.row.original.last_position_gotten_at?.$date;
                      data = (
                        <Text color={textColor} fontSize='sm' fontWeight='700'>
                          {formatLastSeen(unixTime)}
                        </Text>
                      );
                    } else if (cell.column.Header === "EDIT") {
                      data = (
                        <Flex align='center' justify='end' gap='2'>
                          <Button onClick={() => handleEditChip(row.original)} leftIcon={<EditIcon/>} colorScheme='brand'>
                          Edit Chip
                          </Button>
                          <IconButton
                            aria-label={`Slett chip ${chipName}`}
                            icon={<DeleteIcon/>}
                            onClick={() => handleDeleteChip(row.original)}
                            colorScheme='red'
                            variant='outline'
                          />
                        </Flex>
                      );
                    }
                    return (
                      <Td
                        {...cell.getCellProps()}
                        key={index}
                        fontSize={{ sm: "14px" }}
                        minW={{ sm: "150px", md: "200px", lg: "auto" }}
                        borderColor='transparent'>
                        {data}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })
          )}
        </Tbody>
      </Table>
      {isEditOpen && editingChip && (
        <EditChip
          isOpen={isEditOpen}
          onClose={handleEditClose}
          chip={editingChip}
          organizationsData={organizations}
          setAlert={setAlert}
          onChipUpdated={handleChipUpdated}
        />
      )}
    </Card>
  );
}
